import { useParams } from '@tanstack/react-router';
import { IAlarmGroup } from '_/api/alarmGroups/alarmGroups.api';
import { useAlarmGroupSidebarItems } from '_/components/AlarmGroups/useAlarmGroupSidebarItems';
import { useAlarmGroup } from '_/hooks/useAlarmGroups';
import React, { createContext, useContext, ReactNode, useEffect } from 'react';

import { useSidebar, CONTEXT_DEPTH } from './LayoutContext';

interface AlarmGroupContextValue {
  alarmGroupId: string | undefined;
  selectedAlarmGroup: IAlarmGroup | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

const AlarmGroupContext = createContext<AlarmGroupContextValue | undefined>(undefined);

export function AlarmGroupProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{ alarmGroupId?: string }>;
  const alarmGroupId = params.alarmGroupId || '';

  // Fetch the alarm group using React Query
  const { data: selectedAlarmGroup, isLoading, isError, error } = useAlarmGroup(alarmGroupId);

  // Sidebar management
  const { setSidebarItems, registerContext, unregisterContext } = useSidebar();
  const sidebarItems = useAlarmGroupSidebarItems();

  useEffect(() => {
    // Register this context level
    registerContext(CONTEXT_DEPTH.ALARM_GROUP);

    return () => {
      // Unregister on unmount
      unregisterContext(CONTEXT_DEPTH.ALARM_GROUP);
    };
  }, [registerContext, unregisterContext]);

  useEffect(() => {
    // Set sidebar items whenever they change
    if (alarmGroupId && sidebarItems.length > 0) {
      setSidebarItems(sidebarItems, CONTEXT_DEPTH.ALARM_GROUP);
    }
  }, [setSidebarItems, sidebarItems, alarmGroupId]);

  const value: AlarmGroupContextValue = {
    alarmGroupId: alarmGroupId,
    selectedAlarmGroup: (selectedAlarmGroup as IAlarmGroup) || null,
    isLoading: isLoading,
    isError: isError,
    error: error as Error | null,
  };

  return <AlarmGroupContext.Provider value={value}>{children}</AlarmGroupContext.Provider>;
}

export function useAlarmGroupContext() {
  const context = useContext(AlarmGroupContext);
  if (context === undefined) {
    throw new Error('useAlarmGroupContext must be used within an AlarmGroupProvider');
  }
  return context;
}
